import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { COUNTRIES, Field, inputCls, money, LegalFooter } from './shared';

const NUMS = ['potential_income', 'current_income', 'years_marriage', 'years_full_dedication', 'years_partial_dedication', 'age'];

export const CompensationForm = ({ country }) => {
  const { api } = useAuth();
  const [f, setF] = useState({ potential_income: '', current_income: '', years_marriage: '', years_full_dedication: '', years_partial_dedication: '', age: 40, health_issues: false, no_pension_contributions: false });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const cur = COUNTRIES.find((c) => c.code === country)?.currency;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { ...f, country };
      NUMS.forEach((k) => { body[k] = Number(f[k] || 0); });
      const { data } = await api.post('/family/compensation', body);
      setRes(data);
    } catch (err) {
      setRes(null);
      toast.error(err.response?.data?.detail || 'No se pudo calcular');
    } finally {
      setLoading(false);
    }
  };

  const num = (k, label, hint) => (
    <Field label={label} hint={hint}>
      <input data-testid={`ec-${k}`} type="number" min="0" step="any" value={f[k]} onChange={set(k)} className={inputCls} />
    </Field>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4" data-testid="compensation-form">
        {num('potential_income', 'Ingreso mensual que pudo percibir', 'Lo que ganaría según su profesión u oficio si no se hubiera dedicado al hogar')}
        {num('current_income', 'Ingreso mensual actual')}
        <div className="grid grid-cols-2 gap-3">
          {num('years_marriage', 'Años de matrimonio')}
          {num('age', 'Edad del cónyuge')}
          {num('years_full_dedication', 'Años sin trabajar', 'Dedicación exclusiva al hogar o hijos')}
          {num('years_partial_dedication', 'Años trabajando menos', 'Jornada parcial o menor remuneración')}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input data-testid="ec-health" type="checkbox" checked={f.health_issues} onChange={set('health_issues')} /> Tiene problemas de salud
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input data-testid="ec-pension" type="checkbox" checked={f.no_pension_contributions} onChange={set('no_pension_contributions')} /> Tiene lagunas previsionales
        </label>
        <button data-testid="ec-submit" disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-rose-400 transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Estimar compensación
        </button>
      </form>
      {res && (
        <div data-testid="ec-result" className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5">
          <p className="text-xs uppercase tracking-wider text-rose-300">Compensación estimada</p>
          <p data-testid="ec-amount" className="mt-1 text-4xl font-bold text-white font-['Outfit']">{money(res.estimated_amount, cur)}</p>
          <p className="text-sm text-slate-400">Rango probable: {money(res.range_low, cur)} – {money(res.range_high, cur)}</p>
          <dl className="mt-4 space-y-2 text-sm">
            <div><dt className="text-slate-500">Ingresos dejados de percibir</dt><dd className="text-white">{money(res.lost_income, cur)}</dd></div>
            <div><dt className="text-slate-500">Factor de menoscabo aplicado</dt><dd className="text-white">{res.factor_pct}%</dd></div>
          </dl>
          {res.adjustments.length > 0 && (
            <ul className="mt-3 list-disc pl-5 text-sm text-slate-300">{res.adjustments.map((a) => <li key={a}>{a}</li>)}</ul>
          )}
          <LegalFooter basis={res.legal_basis} disclaimer={res.disclaimer} />
        </div>
      )}
    </div>
  );
};
